/**
 * @fileoverview Suite de validação por métrica.
 *
 * Como rodar: abre o DevTools e digita:
 *
 *   __validateMultiUnidades()
 *
 * Cada métrica é validada individualmente contra um dataset de
 * referência pequeno e controlado, onde os valores esperados são
 * calculáveis "à mão". Garante:
 *   - sem NaN
 *   - sem undefined
 *   - valores batem com cenários conhecidos
 *
 * Roda também smoke tests sobre o dataset real (50k msgs) verificando
 * tipos, ranges e ausência de NaN nas saídas.
 */

import {
  openNoReplyCount,
  longestNoReplyAttendant,
  avgFirstReplyAttendantSec,
  longestFirstReply,
  avgFirstReplyClientSec,
  totalOpenCount,
  waitingClientCount,
  avgInteractionTimeSec,
  totalTodayCount,
  computeKpis,
  computePerformanceBySmartCenter,
  computeHistorical,
  computeSmsCounters,
  filterConversations,
  getOpenCasesForUnit,
} from "./metrics";

// ── Utilitários de teste ──────────────────────────────────────────────
const results = [];

const fmtResult = (name, passed, detail) => ({ name, passed, detail });

const assertEqual = (name, actual, expected, tolerance = 0) => {
  const ok = Math.abs(Number(actual) - Number(expected)) <= tolerance;
  return fmtResult(name, ok, `actual=${actual} expected=${expected}${tolerance ? ` ±${tolerance}` : ""}`);
};

const assertFinite = (name, value) => {
  const ok = Number.isFinite(Number(value));
  return fmtResult(name, ok, `value=${value}`);
};

const assertInRange = (name, value, min, max) => {
  const v = Number(value);
  const ok = Number.isFinite(v) && v >= min && v <= max;
  return fmtResult(name, ok, `value=${value} range=[${min}, ${max}]`);
};

const assertNotNaN = (name, value) => {
  const ok = !Number.isNaN(value);
  return fmtResult(name, ok, `value=${value}`);
};

// ── Dataset de referência: 10 conversas com cenários controlados ──────
/**
 * Construo um dataset minúsculo onde sei manualmente o resultado esperado.
 * Timestamps em ms; now = 2026-04-23T14:00:00Z (referência arbitrária).
 */
const NOW = new Date("2026-04-23T14:00:00Z");
const T = (offsetSec) => NOW.getTime() - offsetSec * 1000;

const FIXTURE_CONVS = [
  // 1) Aberta, no_reply, cliente esperando há 600s (10min)
  {
    id: "c1", smartCenterId: "sc-1", subsidiaryId: "sub-1", companyId: "co-1",
    agentId: "a1", customerId: "cu1", startedAt: T(600),
    endedAt: null, status: "no_reply",
    firstClientAt: T(600), firstAgentReplyAt: null, secondClientAt: null,
    lastMsgAt: T(600), lastMsgRole: "client", messageCount: 1,
  },
  // 2) Aberta, no_reply, cliente esperando há 1800s (30min) — VAI ser o pior
  {
    id: "c2", smartCenterId: "sc-2", subsidiaryId: "sub-1", companyId: "co-1",
    agentId: "a2", customerId: "cu2", startedAt: T(1800),
    endedAt: null, status: "no_reply",
    firstClientAt: T(1800), firstAgentReplyAt: null, secondClientAt: null,
    lastMsgAt: T(1800), lastMsgRole: "client", messageCount: 1,
  },
  // 3) Aberta, waiting_client (agente respondeu por último)
  //    Cliente mandou em -7200s, agente em -7000s (200s pra responder)
  {
    id: "c3", smartCenterId: "sc-1", subsidiaryId: "sub-1", companyId: "co-1",
    agentId: "a1", customerId: "cu3", startedAt: T(7200),
    endedAt: null, status: "waiting_client",
    firstClientAt: T(7200), firstAgentReplyAt: T(7000), secondClientAt: null,
    lastMsgAt: T(7000), lastMsgRole: "agent", messageCount: 2,
  },
  // 4) Aberta, waiting_agent: cliente mandou outra msg
  //    Cliente em -10800, agente em -10600 (200s), cliente de novo em -10400
  {
    id: "c4", smartCenterId: "sc-1", subsidiaryId: "sub-1", companyId: "co-1",
    agentId: "a1", customerId: "cu4", startedAt: T(10800),
    endedAt: null, status: "waiting_agent",
    firstClientAt: T(10800), firstAgentReplyAt: T(10600), secondClientAt: T(10400),
    lastMsgAt: T(10400), lastMsgRole: "client", messageCount: 3,
  },
  // 5) Fechada com duração de 1000s
  {
    id: "c5", smartCenterId: "sc-2", subsidiaryId: "sub-1", companyId: "co-1",
    agentId: "a2", customerId: "cu5", startedAt: T(20000),
    endedAt: T(19000), status: "closed",
    firstClientAt: T(20000), firstAgentReplyAt: T(19900), secondClientAt: T(19800),
    lastMsgAt: T(19000), lastMsgRole: "agent", messageCount: 4,
  },
  // 6) Fechada com duração de 2000s (delay 1ª resp atendente 50s)
  {
    id: "c6", smartCenterId: "sc-3", subsidiaryId: "sub-2", companyId: "co-3",
    agentId: "a3", customerId: "cu6", startedAt: T(30000),
    endedAt: T(28000), status: "closed",
    firstClientAt: T(30000), firstAgentReplyAt: T(29950), secondClientAt: T(29900),
    lastMsgAt: T(28000), lastMsgRole: "agent", messageCount: 5,
  },
  // 7) Iniciada HOJE (offset 100s), aberta no_reply
  {
    id: "c7", smartCenterId: "sc-3", subsidiaryId: "sub-2", companyId: "co-3",
    agentId: "a3", customerId: "cu7", startedAt: T(100),
    endedAt: null, status: "no_reply",
    firstClientAt: T(100), firstAgentReplyAt: null, secondClientAt: null,
    lastMsgAt: T(100), lastMsgRole: "client", messageCount: 1,
  },
];

// ── Validators ────────────────────────────────────────────────────────
const runFixtureTests = () => {
  // Métrica 1: openNoReplyCount → c1, c2, c7 = 3
  results.push(assertEqual(
    "openNoReplyCount",
    openNoReplyCount(FIXTURE_CONVS),
    3,
  ));

  // Métrica 2: longestNoReplyAttendant → c2 (1800s)
  const longestNoReply = longestNoReplyAttendant(FIXTURE_CONVS, NOW);
  results.push(assertEqual("longestNoReplyAttendant.seconds", longestNoReply.seconds, 1800));
  results.push(fmtResult("longestNoReplyAttendant.smartCenterId", longestNoReply.smartCenterId === "sc-2", `value=${longestNoReply.smartCenterId}`));

  // Métrica 3: avgFirstReplyAttendantSec
  // c3=200, c4=200, c5=100, c6=50 → média = (200+200+100+50)/4 = 137.5 → 138 (round)
  results.push(assertEqual(
    "avgFirstReplyAttendantSec",
    avgFirstReplyAttendantSec(FIXTURE_CONVS),
    138,
    1,
  ));

  // Métrica 4: longestFirstReply
  // delay máximo: c3 ou c4 = 200s
  results.push(assertEqual("longestFirstReply.seconds", longestFirstReply(FIXTURE_CONVS).seconds, 200));

  // Métrica 5: avgFirstReplyClientSec
  // c4: secondClientAt - firstAgentReplyAt = T(10400) - T(10600) = +200s
  // c5: T(19800) - T(19900) = +100s
  // c6: T(29900) - T(29950) = +50s
  // média = (200+100+50)/3 = 116.66 → 117
  results.push(assertEqual("avgFirstReplyClientSec", avgFirstReplyClientSec(FIXTURE_CONVS), 117, 1));

  // Métrica 6: totalOpenCount = 5 (c1-c4 + c7)
  results.push(assertEqual("totalOpenCount", totalOpenCount(FIXTURE_CONVS), 5));

  // Métrica 7: waitingClientCount = 1 (c3)
  results.push(assertEqual("waitingClientCount", waitingClientCount(FIXTURE_CONVS), 1));

  // Métrica 8: avgInteractionTimeSec
  // c5: endedAt - startedAt = (NOW-19000) - (NOW-20000) = 1000s
  // c6: 30000-28000 = 2000s
  // média = 1500s
  results.push(assertEqual("avgInteractionTimeSec", avgInteractionTimeSec(FIXTURE_CONVS), 1500));

  // Métrica 9: totalTodayCount
  // Considera startOfDay(NOW). c7 (offset 100s, mesmo dia) deveria contar.
  // c1, c2, c3 (offsets 600, 1800, 7200) também são "hoje" se NOW estiver bem após
  // medianoite. NOW = 2026-04-23T14:00:00Z, então startOfDay = 2026-04-23T00:00:00 (local).
  // Offset 600s = 13:50. Offset 1800s = 13:30. Offset 7200s = 12:00. Offset 10800s = 11:00.
  // Offset 20000s = ~08:30. Offset 30000s = ~05:40. Offset 100s = 13:58:20.
  // Todos os timestamps estão no MESMO DIA do NOW. Total = 7.
  // (Dependendo do TZ pode variar 1; assertion tolerante.)
  const todayCount = totalTodayCount(FIXTURE_CONVS, NOW);
  results.push(assertInRange("totalTodayCount", todayCount, 5, 7));

  // Filtro: filterConversations(period=24h) deve pegar todos os 7
  const filtered24h = filterConversations({
    conversations: FIXTURE_CONVS,
    filters: { period: "today" },
    now: NOW,
  });
  // offset máximo dos fixtures = 30000s = 8.33h. Tudo cabe em 24h.
  results.push(assertEqual("filter.period.today", filtered24h.length, 7));

  // Filtro: subsidiária sub-2 → só c6 e c7
  const filteredSub = filterConversations({
    conversations: FIXTURE_CONVS,
    filters: { period: "12m", subsidiary: "sub-2" },
    now: NOW,
  });
  results.push(assertEqual("filter.subsidiary.sub-2", filteredSub.length, 2));

  // Performance por SC: para sc-1 deve ter totalOpen=3 (c1, c3, c4)
  const perf = computePerformanceBySmartCenter({
    conversations: FIXTURE_CONVS,
    smartCenters: [
      { id: "sc-1", name: "SC One", ascCode: "1234567", companyId: "co-1", subsidiaryId: "sub-1" },
      { id: "sc-2", name: "SC Two", ascCode: "2234567", companyId: "co-1", subsidiaryId: "sub-1" },
      { id: "sc-3", name: "SC Three", ascCode: "3234567", companyId: "co-3", subsidiaryId: "sub-2" },
    ],
    now: NOW,
  });
  const sc1 = perf.find((r) => r.id === "sc-1");
  results.push(assertEqual("perf.sc-1.totalOpen", sc1?.totalOpen, 3));
  // sc-2: c2 (no_reply) + c5 (fechada) → totalOpen=1
  const sc2 = perf.find((r) => r.id === "sc-2");
  results.push(assertEqual("perf.sc-2.totalOpen", sc2?.totalOpen, 1));

  // computeKpis: sanity — todos devem ter `value` definido e não-NaN
  const smartCentersById = new Map([
    ["sc-1", { id: "sc-1", name: "SC One" }],
    ["sc-2", { id: "sc-2", name: "SC Two" }],
    ["sc-3", { id: "sc-3", name: "SC Three" }],
  ]);
  const kpis = computeKpis({ conversations: FIXTURE_CONVS, now: NOW, smartCentersById });
  results.push(assertEqual("kpis.length", kpis.length, 9));
  kpis.forEach((k) => {
    results.push(fmtResult(
      `kpi[${k.key}].value`,
      typeof k.value === "string" && k.value.length > 0 && k.value !== "NaN",
      `value=${JSON.stringify(k.value)}`,
    ));
    if (k.delta) {
      results.push(assertNotNaN(`kpi[${k.key}].delta.pct`, k.delta.pct));
    }
  });

  // Histórico
  const hist = computeHistorical({ conversations: FIXTURE_CONVS, now: NOW });
  results.push(assertEqual("charts.openNoReply.length", hist.openNoReply.length, 12));
  // Todos os pontos devem ter .value finito
  Object.entries(hist).forEach(([k, series]) => {
    const allFinite = series.every((p) => Number.isFinite(p.value));
    results.push(fmtResult(`charts.${k}.all-finite`, allFinite, `series=${k}`));
  });

  // SMS counters
  const counters = computeSmsCounters({ conversations: FIXTURE_CONVS, now: NOW });
  // c1 (no_reply 10min) → nível 2 (10-15min)
  // c2 (no_reply 30min) → nível 5 (>30min, mas exato 30 cai em 30+ → 5)
  // c3 (firstAgentReply 200s = 3.3min) → < 5min, não conta
  // c4 (delay 200s = 3.3min) → < 5min, não conta
  // c5 (delay 100s) → não conta
  // c6 (delay 50s) → não conta
  // c7 (no_reply 100s) → < 5min, não conta
  // Esperado: counters[2] = 1, counters[5] = 1 (porque 30min/exatos → "< 30" = false, vai pra >=30 = 5)
  // Mas no código: minutes < 30 → 4, else 5. 30 minutos exatos: minutes = 30 → entra no else → 5.
  results.push(assertEqual("sms.counters[2]", counters[2], 1));
  results.push(assertEqual("sms.counters[5]", counters[5], 1));

  // Open cases for unit sc-1: c1, c3, c4 (3 abertas)
  const cases = getOpenCasesForUnit({ conversations: FIXTURE_CONVS, smartCenterId: "sc-1", now: NOW });
  results.push(assertEqual("getOpenCasesForUnit.sc-1.count", cases.length, 3));
};

// ── Smoke tests no dataset real (50k msgs) ────────────────────────────
const runRealDatasetSmokeTests = () => {
  const dataset = (typeof window !== "undefined") ? window.__multiUnidadesDataset : null;
  if (!dataset) {
    results.push(fmtResult("real.dataset.exists", false, "window.__multiUnidadesDataset not set — abra a página Multi unidades primeiro"));
    return;
  }
  results.push(assertInRange("real.companies", dataset.companies.length, 5, 5));
  results.push(assertInRange("real.smartCenters", dataset.smartCenters.length, 30, 30));
  results.push(assertInRange("real.agents", dataset.agents.length, 100, 100));
  results.push(assertInRange("real.customers", dataset.customers.length, 1000, 1000));
  results.push(assertInRange("real.conversations", dataset.conversations.length, 10000, 10000));
  results.push(assertInRange("real.messages", dataset.messages.length, 30000, 70000));

  // Smoke metrics
  const conv = dataset.conversations;
  const now = dataset.now;
  results.push(assertFinite("real.openNoReplyCount", openNoReplyCount(conv)));
  const longest = longestNoReplyAttendant(conv, now);
  results.push(assertFinite("real.longestNoReplyAttendant.seconds", longest.seconds));
  results.push(assertFinite("real.avgFirstReplyAttendantSec", avgFirstReplyAttendantSec(conv)));
  results.push(assertFinite("real.longestFirstReply.seconds", longestFirstReply(conv).seconds));
  results.push(assertFinite("real.avgFirstReplyClientSec", avgFirstReplyClientSec(conv)));
  results.push(assertFinite("real.totalOpenCount", totalOpenCount(conv)));
  results.push(assertFinite("real.waitingClientCount", waitingClientCount(conv)));
  results.push(assertFinite("real.avgInteractionTimeSec", avgInteractionTimeSec(conv)));
  results.push(assertFinite("real.totalTodayCount", totalTodayCount(conv, now)));

  // Performance: cada row tem todos os campos numéricos finitos
  const perf = computePerformanceBySmartCenter({
    conversations: conv,
    smartCenters: dataset.smartCenters,
    now,
  });
  results.push(assertEqual("real.performance.length", perf.length, dataset.smartCenters.length));
  const badRow = perf.find((r) => !Number.isFinite(r.totalOpen) || !Number.isFinite(r.avgFirstReplyAttendantSec));
  results.push(fmtResult("real.performance.all-rows-finite", !badRow, badRow ? JSON.stringify(badRow) : "ok"));

  // Histórico
  const hist = computeHistorical({ conversations: conv, now });
  Object.entries(hist).forEach(([k, series]) => {
    const bad = series.find((p) => !Number.isFinite(p.value));
    results.push(fmtResult(`real.charts.${k}.all-finite`, !bad, bad ? `bad point=${JSON.stringify(bad)}` : "ok"));
  });
};

// ── Runner exportado ──────────────────────────────────────────────────
export const runValidation = () => {
  results.length = 0;
  runFixtureTests();
  runRealDatasetSmokeTests();
  const passed = results.filter((r) => r.passed).length;
  const failed = results.length - passed;

  // eslint-disable-next-line no-console
  console.groupCollapsed(`[MultiUnidades] Validation: ${passed}/${results.length} passed, ${failed} failed`);
  results.forEach((r) => {
    // eslint-disable-next-line no-console
    const log = r.passed ? console.log : console.error;
    log(`${r.passed ? "✓" : "✗"} ${r.name}  ·  ${r.detail}`);
  });
  // eslint-disable-next-line no-console
  console.groupEnd();
  return { total: results.length, passed, failed, results: [...results] };
};

// Expose globalmente pra rodar do DevTools
if (typeof window !== "undefined") {
  window.__validateMultiUnidades = runValidation;
}
